import type { ArtifactLine, ArtifactSourceLine, ArtifactSurface, ArtifactTheme, ExecutionArtifactSpec } from '../model.js';
import { executionArtifactLineId } from '../families/execution-artifact.js';

// Terminal, editor and query inspector share these lines byte-for-byte. The
// evidence surface changes presentation, never evidence identity or content.
const EXPLAIN_LINES: ArtifactLine[] = [
  { id: 'command', kind: 'command', text: 'psql -f slow-orders.sql' },
  { id: 'plan-heading', kind: 'muted', text: 'QUERY PLAN' },
  { id: 'hash-join', kind: 'output', text: 'Hash Join  (cost=18.14..42.58 rows=127 width=36) (actual time=0.183..1.592 rows=121 loops=1)' },
  { id: 'hash-condition', kind: 'output', text: '  Hash Cond: (o.customer_id = c.id)' },
  { id: 'join-buffers', kind: 'evidence', text: '  Buffers: shared hit=34' },
  { id: 'orders-scan', kind: 'output', text: '  ->  Seq Scan on orders o  (cost=0.00..22.70 rows=127 width=16) (actual time=0.012..1.211 rows=121 loops=1)' },
  { id: 'orders-filter', kind: 'output', text: "        Filter: (status = 'pending'::text)" },
  { id: 'rows-removed', kind: 'evidence', text: '        Rows Removed by Filter: 879' },
  { id: 'customers-hash', kind: 'output', text: '  ->  Hash  (cost=13.06..13.06 rows=406 width=28) (actual time=0.151..0.152 rows=406 loops=1)' },
  { id: 'customers-scan', kind: 'output', text: '        ->  Seq Scan on customers c  (cost=0.00..13.06 rows=406 width=28) (actual time=0.006..0.071 rows=406 loops=1)' },
  { id: 'planning-time', kind: 'muted', text: 'Planning Time: 0.286 ms' },
  { id: 'execution-time', kind: 'evidence', text: 'Execution Time: 1.742 ms' },
];

const SQL_SOURCE_LINES: ArtifactSourceLine[] = [
  { id: 'explain', text: 'EXPLAIN (ANALYZE, BUFFERS)' },
  { id: 'select', text: 'SELECT o.id, c.email' },
  { id: 'from', text: 'FROM orders AS o' },
  { id: 'join', text: 'JOIN customers AS c' },
  { id: 'join-condition', text: '  ON c.id = o.customer_id' },
  { id: 'where', text: 'WHERE o.status = \'pending\';' },
];

const PROFILE_LINES: ArtifactLine[] = [
  { id: 'command', kind: 'command', text: 'go tool pprof -top -nodecount=20 ./kiln.test ./cpu.pprof' },
  { id: 'file', kind: 'muted', text: 'File: kiln.test' },
  { id: 'type', kind: 'muted', text: 'Type: cpu' },
  { id: 'duration', kind: 'muted', text: 'Duration: 2.19s, Total samples = 2.04s (93.15%)' },
  { id: 'accounting', kind: 'evidence', text: 'Showing nodes accounting for 1.91s, 93.63% of 2.04s total' },
  { id: 'dropped', kind: 'muted', text: 'Dropped 51 nodes (cum <= 0.01s)' },
  { id: 'heading', kind: 'muted', text: '      flat  flat%   sum%        cum   cum%' },
  { id: 'scan-object', kind: 'output', text: '     0.42s 20.59% 20.59%      0.42s 20.59%  runtime.scanobject' },
  { id: 'mallocgc', kind: 'evidence', text: '     0.31s 15.20% 35.78%      0.83s 40.69%  runtime.mallocgc' },
  { id: 'mapassign', kind: 'output', text: '     0.23s 11.27% 47.06%      0.31s 15.20%  runtime.mapassign_faststr' },
  { id: 'memmove', kind: 'output', text: '     0.18s  8.82% 55.88%      0.18s  8.82%  runtime.memmove' },
  { id: 'compile-node', kind: 'evidence', text: '     0.16s  7.84% 63.73%      1.41s 69.12%  kiln/viewcompiler.compileNode' },
  { id: 'find-object', kind: 'output', text: '     0.13s  6.37% 70.10%      0.21s 10.29%  runtime.findObject' },
  { id: 'typedmemmove', kind: 'output', text: '     0.11s  5.39% 75.49%      0.19s  9.31%  runtime.typedmemmove' },
  { id: 'growslice', kind: 'output', text: '     0.09s  4.41% 79.90%      0.27s 13.24%  runtime.growslice' },
  { id: 'resolve-labels', kind: 'output', text: '     0.08s  3.92% 83.82%      0.36s 17.65%  kiln/viewcompiler.resolveLabels' },
  { id: 'gc-assist', kind: 'output', text: '     0.07s  3.43% 87.25%      0.49s 24.02%  runtime.gcAssistAlloc' },
  { id: 'shape-text', kind: 'output', text: '     0.06s  2.94% 90.20%      0.22s 10.78%  kiln/scene.(*Builder).Text' },
  { id: 'mapaccess', kind: 'output', text: '     0.04s  1.96% 92.16%      0.04s  1.96%  runtime.mapaccess2_faststr' },
  { id: 'other', kind: 'muted', text: '     0.03s  1.47% 93.63%      0.03s  1.47%  other' },
];

/** Backwards-compatible catalogue/lesson entry point. */
export function executionArtifactFixture(width = 640, stress = false, focusLineId?: string, surface?: ArtifactSurface, theme: ArtifactTheme = 'light', outputOpen = true): ExecutionArtifactSpec {
  const resolvedSurface = surface ?? (stress ? 'profiler' : 'terminal');
  return createFixture(resolvedSurface, width, focusLineId, stress || resolvedSurface === 'profiler', theme, outputOpen);
}

/** Explicit evidence-register fixture for surface bake-offs. */
export function executionArtifactSurfaceFixture(surface: ArtifactSurface, width = 640, focusLineId?: string, theme: ArtifactTheme = 'light', outputOpen = true): ExecutionArtifactSpec {
  return createFixture(surface, width, focusLineId, surface === 'profiler', theme, outputOpen);
}

export function executionArtifactVariations(width = 640): ExecutionArtifactSpec[] {
  return [false, true].map((outputOpen) => executionArtifactSurfaceFixture('editor', width, undefined, 'light', outputOpen));
}

function createFixture(surface: ArtifactSurface, width: number, focusLineId: string | undefined, profile: boolean, theme: ArtifactTheme = 'light', outputOpen = true): ExecutionArtifactSpec {
  const context = surface === 'terminal'
    ? { workingDirectory: '~/kiln/lessons/sql', tabLabel: 'psql', exitCode: 0 }
    : surface === 'editor'
      ? { workingDirectory: '~/kiln/lessons/sql', fileName: 'slow-orders.sql', language: 'sql', tabLabel: 'slow-orders.sql', exitCode: 0, theme, outputOpen }
      : surface === 'query-inspector'
        ? { workingDirectory: 'kiln_lesson', tabLabel: 'Actual execution plan', exitCode: 0 }
        : { workingDirectory: '~/kiln/bench', fileName: 'cpu.pprof', tabLabel: 'CPU · Top', exitCode: 0 };
  const title = surface === 'terminal'
    ? 'psql · EXPLAIN ANALYZE'
    : surface === 'editor'
      ? 'slow-orders.sql · Run output'
      : surface === 'query-inspector'
        ? 'Query inspector · Actual plan'
        : 'CPU profile · compile benchmark';
  return {
    id: profile ? 'execution-artifact-profiler-stress' : `execution-artifact-explain-${surface}`,
    family: 'execution-artifact',
    view: 'artifact',
    viewport: { width, height: surface === 'editor' && width <= 390 ? 540 : 470 },
    artifactKind: profile ? 'profile' : 'explain',
    surface,
    context,
    title,
    ...(surface === 'editor' ? { source: { fileName: 'slow-orders.sql', language: 'sql' as const, lines: SQL_SOURCE_LINES.map((line) => ({ ...line })), activeLine: 6 } } : {}),
    lines: (profile ? PROFILE_LINES : EXPLAIN_LINES).map((line) => ({ ...line })),
    ...(focusLineId ? { focusLineId } : {}),
    provenance: profile
      ? 'Captured Go pprof -top fixture · Kiln view-compiler benchmark run · line text retained verbatim'
      : 'Captured PostgreSQL EXPLAIN (ANALYZE, BUFFERS) fixture · Kiln lesson database · shared evidence retained verbatim across surfaces',
  };
}

export const defaultExecutionArtifactFocus = executionArtifactLineId('execution-time');
export const stressExecutionArtifactFocus = executionArtifactLineId('mallocgc');
export const executionArtifactGuidedTargets = {
  command: executionArtifactLineId('command'),
  filterCost: executionArtifactLineId('rows-removed'),
  totalRuntime: defaultExecutionArtifactFocus,
  profileHotspot: stressExecutionArtifactFocus,
} as const;
